import { makeStyles } from '@material-ui/styles'
import { ephemeralStorageDoc, storageProvisioning } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import UsageWidget from 'core/components/widgets/UsageWidget'
import { METRICS_API_KEYS } from 'core/components/widgets/useTrendData'
import Divider from 'core/elements/Divider'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { useCallback, useMemo, useState } from 'react'
import { aggregateCinderPools, getHostAllocationTotals, getHostUsageTotals } from './helpers'
import LearnMoreLink from 'core/components/LearnMoreLink'
import { formatMetricValue } from 'core/utils/unitUtils'

interface Props {
  hosts: any[]
  hypervisors?: any[]
  storagePools?: any[]
  perHostMetrics?: boolean
  showAllocation?: boolean
  showOnlyForSelectedHosts?: boolean
  showEphemeralStorage?: boolean
  showBlockStorage?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 8,
  },
  hostCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: theme.spacing(1.5),
    margin: theme.spacing(2, 0),
  },
  sectionHeader: {
    backgroundColor: theme.components.card.background,
    borderRadius: '4px 4px 2px 2px',
    border: `1px solid ${theme.components.card.border}`,
    padding: '16px 24px',
  },
  tooltip: {
    marginLeft: 5,
  },
  horizontalDivider: {
    margin: theme.spacing(1, 0),
  },
  tooltipContent: {
    maxWidth: '300px',
    padding: theme.spacing(1),
  },
  mt1: {
    marginTop: theme.spacing(1),
  },
  inlineFlexCenter: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  provisionedTooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5),

    '& p': {
      color: theme.palette.grey[50],
    },
  },
  overProvisionedInfo: {
    marginTop: theme.spacing(0.5),
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  inlineDropdown: {
    '& .placeholder': {
      fontWeight: 200,
    },
  },
}))

export default function HostsListPageHeader({
  hosts,
  hypervisors = [],
  storagePools = [],
  perHostMetrics = false,
  showAllocation = false,
  showOnlyForSelectedHosts = false,
  showEphemeralStorage = true,
  showBlockStorage = true,
}: Props) {
  const isPersistentStorageRole = hosts?.some((host) =>
    host?.displayRoles?.includes('Persistent Storage'),
  )
  const classes = useStyles()
  const [selectedBackend, setSelectedBackend] = useState(0)

  const ephemeralStorageTooltipContent = (
    <div className={classes.tooltipContent}>
      Shows the current % utilization of Ephemeral Storage across all hypervisor hosts in this
      region. <ExternalLink url={ephemeralStorageDoc}>What is ephemeral storage</ExternalLink>
    </div>
  )

  const totals = getHostUsageTotals(hosts)
  const storageMetrics = useMemo(() => aggregateCinderPools(storagePools), [storagePools])

  const allocation = useMemo(() => {
    if (!showAllocation) {
      return {
        compute: {
          current: 0,
          max: 0,
          percent: 0,
        },
        memory: {
          current: 0,
          max: 0,
          percent: 0,
        },
        ephemeralStorage: {
          current: 0,
          max: 0,
          percent: 0,
        },
      }
    }
    return getHostAllocationTotals({ hosts, hypervisors })
  }, [hosts, hypervisors, storagePools, showAllocation])

  const hostIds = useMemo(() => hosts.map((host) => host.id), [hosts])

  const backendDropdownItems = useMemo(
    () =>
      storageMetrics?.map((backend, idx) => ({
        key: backend.backend,
        label: backend.backend,
        value: idx,
      })) ?? [],
    [storageMetrics],
  )

  // Helper function to generate storage tooltip content
  const getStorageTooltipContent = useCallback(
    (selectedBackend: number) => () => {
      const {
        formattedValue: provisionedCapcityFormatted,
        displayUnit: provisionedCapcityDisplayUnit,
      } = formatMetricValue(storageMetrics[selectedBackend].provisionedCapacity, 'GiB')

      const {
        formattedValue: totalCapcityFormatted,
        displayUnit: totalCapcityDisplayUnit,
      } = formatMetricValue(storageMetrics[selectedBackend].allocation.max, 'GiB')

      return (
        <div className={classes.provisionedTooltipContent}>
          <Text variant="body2" component="p">
            This backend is{' '}
            <b>{storageMetrics[selectedBackend]?.isOverProvisioned ? 'Thin' : 'Thick'}</b>{' '}
            Provisioned. <LearnMoreLink url={storageProvisioning} />
          </Text>
          {storageMetrics[selectedBackend]?.isOverProvisioned && (
            <div className={classes.overProvisionedInfo}>
              <Text variant="body2" component="p">
                Total Capacity:{' '}
                <b>
                  {totalCapcityFormatted} {totalCapcityDisplayUnit}
                </b>
              </Text>
              <Text variant="body2" component="p">
                Provisioned Capacity:{' '}
                <b>
                  {provisionedCapcityFormatted} {provisionedCapcityDisplayUnit}
                </b>
              </Text>
              <Text variant="body2" component="p">
                Current Over Subscription Ratio:{' '}
                <b>
                  {(
                    storageMetrics[selectedBackend]?.provisionedCapacity /
                    storageMetrics[selectedBackend]?.allocation.max
                  ).toFixed(2)}
                </b>
              </Text>
              <Text variant="body2" component="p">
                Max Over Subscription Ratio:{' '}
                <b>{storageMetrics[selectedBackend]?.maxOverSubscriptionRatio}</b>
              </Text>
            </div>
          )}
        </div>
      )
    },
    [storageMetrics],
  )

  const showBlockStorageWidget =
    showBlockStorage && isPersistentStorageRole && storageMetrics?.length > 0
  const showStorageSection = showBlockStorage || showEphemeralStorage

  return (
    <div className={classes.container}>
      {showAllocation && (
        <>
          <div className={classes.sectionHeader}>
            <Text variant="subtitle2">Allocation</Text>
            <Text variant="body2">
              Shows the total virtualized resources allocated across all the virtual machines
              provisioned on {perHostMetrics ? <b>{hosts?.[0]?.name}</b> : 'the hosts'}.
            </Text>
          </div>
          <div className={classes.hostCards}>
            <UsageWidget
              icon="microchip"
              title="CPU"
              stats={allocation?.compute}
              units="vCPUs"
              allocation
            />
            <UsageWidget
              icon="memory"
              title="Memory"
              stats={allocation?.memory}
              units="GiB"
              allocation
            />
            {showEphemeralStorage && (
              <UsageWidget
                icon="sim-card"
                title="Ephemeral Storage"
                stats={allocation?.ephemeralStorage}
                units="GB"
                allocation
              />
            )}
            {showBlockStorageWidget && (
              <UsageWidget
                icon="coins"
                title={
                  <div className={classes.inlineFlexCenter}>
                    Block Storage
                    <AsyncDropdown
                      compact
                      items={backendDropdownItems}
                      value={selectedBackend}
                      onChange={setSelectedBackend}
                      className={classes.inlineDropdown}
                    />
                  </div>
                }
                dialTooltipMsg={getStorageTooltipContent(selectedBackend)}
                stats={storageMetrics[selectedBackend]?.allocation}
                units="GB"
                allocation
              />
            )}
          </div>
          <div className={classes.sectionHeader}>
            <Text variant="subtitle2">Usage</Text>
            <Text variant="body2">
              Shows total used resources{' '}
              {perHostMetrics ? (
                <>
                  on <b>{hosts?.[0]?.name}</b>
                </>
              ) : (
                'across all hosts'
              )}
              .
            </Text>
          </div>
        </>
      )}
      {/* Compute Section */}
      <Text variant="subtitle2" className={classes.mt1}>
        Compute
      </Text>
      <Divider className={classes.horizontalDivider} />
      <div className={classes.hostCards}>
        <UsageWidget
          icon="microchip"
          title="CPU"
          stats={totals.compute}
          units="GHz"
          graphUnit="vCPUs"
          // primary={`out of ${totals.compute?.max?.toFixed(1)} GHz`}
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_CPU_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_CPU_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_CPU_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
          dialTooltipMsg="Shows the current % utilization of total CPU across all hypervisor hosts in this region."
          chartTooltipMsg="Shows a graph of CPU utilization over the selected period of time, across all hypervisor hosts in this region."
        />
        <UsageWidget
          icon="memory"
          title="Memory"
          stats={totals.memory}
          units="GiB"
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_MEMORY_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_MEMORY_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_MEMORY_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
          dialTooltipMsg="Shows the current % utilization of total Memory across all hypervisor hosts in this region."
          chartTooltipMsg="Shows a graph of Memory utilization over the selected period of time, across all hypervisor hosts in this region."
        />
      </div>
      {/* Storage Section */}
      {showStorageSection && (
        <>
          <div className="flex">
            <Text variant="subtitle2">Storage</Text>
          </div>
          <Divider className={classes.horizontalDivider} />
          <div className={classes.hostCards}>
            {showEphemeralStorage && (
              <UsageWidget
                icon="sim-card"
                title="Ephemeral Storage"
                stats={totals.ephemeralDisk}
                units="GiB"
                graphUnit="%"
                resourceId={hosts[0]?.id}
                hostIds={showOnlyForSelectedHosts ? hostIds : []}
                dialTooltipMsg={() => ephemeralStorageTooltipContent}
                metricKey={
                  perHostMetrics
                    ? METRICS_API_KEYS.PER_HOST_DISK_USAGE
                    : showOnlyForSelectedHosts
                    ? METRICS_API_KEYS.SELECTED_HOST_DISK_USAGE
                    : METRICS_API_KEYS.ALL_HOSTS_DISK_USAGE
                }
              />
            )}
            {showBlockStorageWidget ? (
              <UsageWidget
                icon="coins"
                title={
                  <div className={classes.inlineFlexCenter}>
                    Block Storage
                    <AsyncDropdown
                      compact
                      items={backendDropdownItems}
                      value={selectedBackend}
                      onChange={setSelectedBackend}
                      className={classes.inlineDropdown}
                    />
                  </div>
                }
                stats={storageMetrics[selectedBackend]?.usage}
                units="GiB"
                resourceId={storageMetrics[selectedBackend]?.volume_backend_name}
                hostIds={showOnlyForSelectedHosts ? hostIds : []}
                metricKey={METRICS_API_KEYS.PER_HOST_BLOCK_STORAGE_USAGE}
                graphTitle="Block Storage"
              />
            ) : (
              // Keep this div to maintain the grid layout
              <div />
            )}
          </div>
        </>
      )}
    </div>
  )
}
